import { axiosInstance, API_BASE_URL } from "../config/axiosInstance";

// API Response Types
export interface McGeneralDiseaseItem {
  name: string;
  id: number;
  upn: number;
  gdtid: number | null;
  status: string | null;
  note: string | null;
  cuser: string;
  ctime: string;
  firstdiagdate: string | null;
  ptid: number;
  typeid: number;
  pid: number | null;
  dailyuseamount: number | null;
  typeofmaterial: string | null;
  usagetime: number | null;
  cessationdate: string | null;
  opassname: string | null;
  pdate: string | null;
  dose: string | null;
  allergytype: number | null;
  diseasekind: number | null;
  isupd: number | null;
  ppid: number | null;
  spirometryid: number | null;
  exposuretype: number | null;
  privatenotes: string | null;
  enddate: string | null;
  autoinsert: number | null;
  rarediseasediagnosis: number | null;
  operationtype: number;
  manualdrugname: string | null;
  isactiveallergy: string | null;
  allergylastdate: string | null;
  allergystatus?: string | null;
}

export interface McGeneralDiseaseGroup {
  typename: string;
  ordernumber: number;
  count: number;
  items: McGeneralDiseaseItem[];
}

export interface GetMcGeneralDiseasesResponse {
  data: {
    counts: Record<string, number>;
    totalcount: number;
    groups: McGeneralDiseaseGroup[];
  };
  issuccess: boolean;
  statuscode: number;
  message: string;
}

export interface PastIllnessOption {
  id: number;
  gpid: number;
  code: string | null;
  name: string;
  parentid: number | null;
  isactive: string;
  ordernumber: number | null;
  offid: number | null;
  offname: string | null;
}

export interface GetPastIllnessOptionsResponse {
  data: PastIllnessOption[];
  issuccess: boolean;
  statuscode: number;
  message: string;
}

export const mcGeneralDiseaseService = {
  getMcGeneralDiseases: async (
    ptId: string | number
  ): Promise<{ counts: Record<string, number>; groups: McGeneralDiseaseGroup[] }> => {
    try {
      const response = await axiosInstance.get<GetMcGeneralDiseasesResponse>(
        `${API_BASE_URL}/reference/api/McGeneralDisease/GetMcGeneralDiseases/${ptId}`
      );
      // API response contains data object, extract it
      return {
        counts: response.data.data.counts,
        groups: response.data.data.groups,
      };
    } catch (error) {
      console.error("Error fetching general diseases:", error);
      throw error;
    }
  },

  getPastIllnessOptions: async (
    offId: number = 0
  ): Promise<GetPastIllnessOptionsResponse> => {
    try {
      const response = await axiosInstance.post<GetPastIllnessOptionsResponse>(
        `${API_BASE_URL}/reference/api/McGeneralDisease/field-past-illness`,
        { offId }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching past illness options:", error);
      throw error;
    }
  },

  addPastDisease: async (data: {
    ptid: number;
    processid: number;
    processdate: string;
    note?: string;
  }) => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/McGeneralDisease/past-disease`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error adding past disease:", error);
      throw error;
    }
  },

  addPastSurgery: async (data: {
    ptid: number;
    processid: number;
    processdate: string;
    note?: string;
    opassname?: string;
  }) => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/McGeneralDisease/past-surgery`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error adding past surgery:", error);
      throw error;
    }
  },

  updatePastSurgery: async (data: {
    id: number;
    processdate: string;
    note?: string;
    opassname?: string;
  }): Promise<void> => {
    try {
      await axiosInstance.put(
        `${API_BASE_URL}/reference/api/McGeneralDisease/past-surgery`,
        data
      );
    } catch (error) {
      console.error("Error updating past surgery:", error);
      throw error;
    }
  },

  addFamilyHistory: async (data: {
    ptid: number;
    processid: number;
    note?: string;
  }) => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/McGeneralDisease/family-history`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error adding family history:", error);
      throw error;
    }
  },

  addAllergy: async (data: {
    ptId: number;
    processId: number;
    note?: string;
    allergyStatus?: string | null;
    allergyType: number;
    isActiveAllergy: string;
    allergyLastDate?: string;
  }) => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/McGeneralDisease/allergy`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error adding allergy:", error);
      throw error;
    }
  },

  addDisease: async (data: {
    ptid: number;
    processid: number;
    firstdiagdate: string;
    finishdate?: string;
    spirometryid: number;
    note?: string;
  }) => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/McGeneralDisease/disease`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error adding disease:", error);
      throw error;
    }
  },

  addHabit: async (data: {
    ptid: number;
    processid: number;
    dailyuseamount?: string;
    typeofmaterial?: string;
    usagetime?: string;
    cessationdate?: string;
    note?: string;
  }) => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/McGeneralDisease/habit`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error adding habit:", error);
      throw error;
    }
  },

  updateHabit: async (data: {
    id: number;
    dailyuseamount?: string;
    typeofmaterial?: string;
    usagetime?: string;
    cessationdate?: string;
    note?: string;
  }): Promise<void> => {
    try {
      await axiosInstance.put(
        `${API_BASE_URL}/reference/api/McGeneralDisease/habit`,
        data
      );
    } catch (error) {
      console.error("Error updating habit:", error);
      throw error;
    }
  },

  addMedication: async (data: {
    ptid: number;
    processid: number;
    processdate?: string;
    manualdrugname?: string;
    usagetime?: string;
    dose?: string;
    note?: string;
  }) => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/McGeneralDisease/medication`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error adding medication:", error);
      throw error;
    }
  },

  addExposure: async (data: {
    ptid: number;
    processid: number;
    processdate?: string;
    note?: string;
    exposuretype: string;
  }) => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/McGeneralDisease/exposure`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error adding exposure:", error);
      throw error;
    }
  },

  addSpecialNote: async (data: {
    ptid: number;
    note?: string;
    privatenotes?: string;
  }) => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/McGeneralDisease/special-note`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error adding special note:", error);
      throw error;
    }
  },

  addBloodPressure: async (data: {
    ptid: number;
    greatbloodpressure: number;
    smallbloodpressure: number;
    pulse: number;
    note?: string;
  }) => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/McGeneralDisease/blood-pressure`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error adding blood pressure:", error);
      throw error;
    }
  },

  addRareDisease: async (data: {
    ptid: number;
    processid: number;
    rarediseasediagnosis: number;
    note?: string;
  }) => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/McGeneralDisease/rare-disease`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error adding rare disease:", error);
      throw error;
    }
  },

  deleteGeneralDisease: async (id: number): Promise<void> => {
    try {
      await axiosInstance.delete(
        `${API_BASE_URL}/reference/api/McGeneralDisease/${id}`
      );
    } catch (error) {
      console.error("Error deleting general disease:", error);
      throw error;
    }
  },

  updatePastDisease: async (data: {
    id: number;
    processdate: string;
    note?: string;
  }): Promise<void> => {
    try {
      await axiosInstance.put(
        `${API_BASE_URL}/reference/api/McGeneralDisease/past-disease`,
        data
      );
    } catch (error) {
      console.error("Error updating past disease:", error);
      throw error;
    }
  },

  updateSpecialNote: async (data: {
    id: number;
    note?: string;
    privatenotes?: string;
  }): Promise<void> => {
    try {
      await axiosInstance.put(
        `${API_BASE_URL}/reference/api/McGeneralDisease/special-note`,
        data
      );
    } catch (error) {
      console.error("Error updating special note:", error);
      throw error;
    }
  },

  updateFamilyHistory: async (data: {
    id: number;
    note?: string;
  }): Promise<void> => {
    try {
      await axiosInstance.put(
        `${API_BASE_URL}/reference/api/McGeneralDisease/family-history`,
        data
      );
    } catch (error) {
      console.error("Error updating family history:", error);
      throw error;
    }
  },

  updateAllergy: async (data: {
    id: number;
    note?: string;
    allergystatus: string;
    allergytype: number;
    isactiveallergy: string;
    allergylastdate?: string;
  }): Promise<void> => {
    try {
      await axiosInstance.put(
        `${API_BASE_URL}/reference/api/McGeneralDisease/allergy`,
        data
      );
    } catch (error) {
      console.error("Error updating allergy:", error);
      throw error;
    }
  },

  updateDisease: async (data: {
    id: number;
    firstdiagdate?: string;
    finishdate?: string;
    spirometryid?: number;
    note?: string;
  }): Promise<void> => {
    try {
      await axiosInstance.put(
        `${API_BASE_URL}/reference/api/McGeneralDisease/disease`,
        data
      );
    } catch (error) {
      console.error("Error updating disease:", error);
      throw error;
    }
  },

  updateMedication: async (data: {
    id: number;
    processid: number;
    processdate?: string;
    manualdrugname?: string;
    usagetime?: string;
    dose?: string;
    note?: string;
  }): Promise<void> => {
    try {
      await axiosInstance.put(
        `${API_BASE_URL}/reference/api/McGeneralDisease/medication`,
        data
      );
    } catch (error) {
      console.error("Error updating medication:", error);
      throw error;
    }
  },

  updateExposure: async (data: {
    id: number;
    processdate?: string;
    note?: string;
    exposuretype: string;
  }): Promise<void> => {
    try {
      await axiosInstance.put(
        `${API_BASE_URL}/reference/api/McGeneralDisease/exposure`,
        data
      );
    } catch (error) {
      console.error("Error updating exposure:", error);
      throw error;
    }
  },

  updateBloodPressure: async (data: {
    id: number;
    greatbloodpressure: number;
    smallbloodpressure: number;
    pulse: number;
    note?: string;
  }): Promise<void> => {
    try {
      await axiosInstance.put(
        `${API_BASE_URL}/reference/api/McGeneralDisease/blood-pressure`,
        data
      );
    } catch (error) {
      console.error("Error updating blood pressure:", error);
      throw error;
    }
  },

  updateRareDisease: async (data: {
    id: number;
    rarediseasediagnosis: number;
    note?: string;
  }): Promise<void> => {
    try {
      await axiosInstance.put(
        `${API_BASE_URL}/reference/api/McGeneralDisease/rare-disease`,
        data
      );
    } catch (error) {
      console.error("Error updating rare disease:", error);
      throw error;
    }
  },

  getGeneralPatientInfo: async (ptid: number): Promise<GeneralPatientInfoResponse> => {
    try {
      const response = await axiosInstance.post<GeneralPatientInfoResponse>(
        `${API_BASE_URL}/reference/api/McGeneralDisease/general-patient-info`,
        { ptid }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching general patient info:", error);
      throw error;
    }
  },

  updateGeneralPatientInfo: async (data: {
    ptid: number;
    weight?: number | null;
    patlength?: number | null;
    geneticexpertopinion?: number | null;
    psychologicalstatus?: number | null;
    marriage?: number | null;
    pregnantorlactation?: string | null;
  }): Promise<void> => {
    try {
      await axiosInstance.post(
        `${API_BASE_URL}/reference/api/McGeneralDisease/general-info`,
        data
      );
    } catch (error) {
      console.error("Error updating general patient info:", error);
      throw error;
    }
  },
};

// General Patient Info Types
export interface PsychologicalStatusOption {
  id: number;
  name: string;
}

export interface MarriageOption {
  id: number;
  name: string | null;
}

export interface PregnancyStatusOption {
  name: string;
  value: string;
}

export interface GenExpertOpinionOption {
  id: number;
  parentid: number | null;
  name: string;
  code: string;
  code2: string | null;
}

export interface GeneralPatientInfoData {
  id: number;
  ptid: number;
  weight: number | null;
  patlength: number | null;
  bsa: number | null;
  psychologicalstatus: number | null;
  marriage: number | null;
  geneticexpertopinion: number | null;
  genexpertopinionname: string | null;
  pregnantorlactation: string | null;
  utime: string | null;
  psychologicalstatusoptions: PsychologicalStatusOption[];
  marriageoptions: MarriageOption[];
  pregnancystatusoptions: PregnancyStatusOption[];
  genexpertopinionoptions: GenExpertOpinionOption[];
}

export interface GeneralPatientInfoResponse {
  data: GeneralPatientInfoData;
  issuccess: boolean;
  statuscode: number;
  message: string;
}

